import React from 'react';
import { DivideIcon as LucideIcon, TrendingUp, TrendingDown } from 'lucide-react';
import { formatCurrency, formatNumber, formatPercentage } from '../../utils/currency';

interface StatsCardProps {
  title: string;
  value: number;
  change?: number;
  icon: LucideIcon;
  type: 'number' | 'currency';
  color?: 'blue' | 'green' | 'purple' | 'orange';
  onClick?: () => void;
}

export const StatsCard: React.FC<StatsCardProps> = ({
  title,
  value,
  change,
  icon: Icon,
  type,
  color = 'blue',
  onClick
}) => {
  const isPositiveChange = change && change >= 0;
  
  const colorClasses = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800',
    green: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 border-green-200 dark:border-green-800',
    purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-800',
    orange: 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 border-orange-200 dark:border-orange-800'
  };

  return (
    <div 
      className={`bg-white dark:bg-dark-bg-secondary p-6 rounded-xl shadow-sm border dark:border-dark-border-primary hover:shadow-md transition-all duration-200 min-h-[140px] ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <p className="text-sm font-medium text-gray-600 dark:text-dark-text-secondary truncate flex-1">{title}</p>
        <div className={`p-3 rounded-lg flex-shrink-0 ${colorClasses[color]}`}>
          <Icon className="h-5 w-5 lg:h-6 lg:w-6" />
        </div>
      </div>
      
      <div className="space-y-3">
        <h3 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-dark-text-primary">
          {type === 'currency' ? formatCurrency(value) : formatNumber(value)}
        </h3>
        {change !== undefined && (
          <div className={`flex items-center space-x-1 text-sm ${
            isPositiveChange ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
          }`}>
            {isPositiveChange ? (
              <TrendingUp className="h-4 w-4" />
            ) : (
              <TrendingDown className="h-4 w-4" />
            )}
            <span className="font-medium">{formatPercentage(Math.abs(change))}</span>
          </div>
        )}
      </div>
    </div>
  );
};